import { useCallback, useState } from 'react'

import { useInsightsContext } from '../context/InsightsContext'
import { serialiseFiltersForApi } from '../utils/filterSerialiser'
import { downloadCsv } from '../utils/downloadCsv'
import { useFilters } from './useFilters'

/**
 * Approximate exported data-row count for the success toast: non-empty lines
 * minus the header row. The endpoint streams CSV (no count field), so this is a
 * best-effort line count — fine for a "N rows exported" confirmation.
 */
const countCsvDataRows = (csv: string): number => {
  const lines = csv.split(/\r?\n/).filter((line) => line.length > 0)

  return Math.max(0, lines.length - 1)
}

/**
 * API error codes the CSV export endpoint can return (pbp-api NEX-714). The
 * endpoint guards size before streaming, so an over-large request fails as a
 * clean 400 rather than a truncated download.
 */
export const INSIGHTS_EXPORT_TOO_LARGE = 'insights/export_too_large'
export const INSIGHTS_EXPORT_DATE_RANGE_TOO_LARGE =
  'insights/export_date_range_too_large'

/**
 * A failed CSV export, carrying the Percent API error `code` (if any) so the UI
 * can show a specific, actionable message (e.g. "too large — narrow filters")
 * instead of a generic "try again".
 */
export class InsightsExportError extends Error {
  constructor(public readonly code?: string) {
    super(code ?? 'insights_export_failed')
    this.name = 'InsightsExportError'
  }
}

/**
 * Pull the Percent API error code (`{ error: { code } }`) off a failed export
 * request. The export is requested as text (`responseType: 'text'`), so an error
 * body arrives as a JSON *string* rather than a parsed object — parse defensively.
 */
const exportErrorCode = (error: unknown): string | undefined => {
  const data = (error as { response?: { data?: unknown } })?.response?.data
  let body: unknown = data
  if (typeof data === 'string') {
    try {
      body = JSON.parse(data)
    } catch {
      return undefined
    }
  }

  return (body as { error?: { code?: string } } | undefined)?.error?.code
}

export type ExportCsvArgs = {
  /** Selected column keys, in the order they should appear in the CSV. */
  columns: string[]
  /** Adds every column of these column groups after `columns` (API `columnGroups`). */
  columnGroups?: string[]
  /** Download filename, e.g. `donations-by-employee.csv`. */
  fileName: string
}

export type UseInsightsDatasetExportResult = {
  /** Requests the CSV for the current filters + selected columns and downloads it; resolves to the exported row count. */
  exportCsv: (args: ExportCsvArgs) => Promise<number>
  isExporting: boolean
  /**
   * Whether the host service implements the export endpoint (NEX-712 PR 2). The
   * UI uses this to disable the action until the backend is wired.
   */
  canExport: boolean
}

/**
 * Imperative CSV export for a dataset. Reads the live filter values from the
 * surrounding `<Insights.Domain>` (so the export matches what the report shows),
 * serialises them with the selected `columns` projection, and calls the host's
 * `exportDataset` service method, then triggers the file download.
 *
 * The export endpoint itself ships in pbp-api PR 2 of NEX-712; until the host
 * wires `service.exportDataset`, `canExport` is false and `exportCsv` rejects.
 */
export const useInsightsDatasetExport = (
  dataset: string
): UseInsightsDatasetExportResult => {
  const { domain, service } = useInsightsContext()
  const { values: filters } = useFilters()
  const [isExporting, setIsExporting] = useState(false)

  const canExport = typeof service.exportDataset === 'function'

  const exportCsv = useCallback(
    async ({
      columns,
      columnGroups,
      fileName,
    }: ExportCsvArgs): Promise<number> => {
      if (!service.exportDataset) {
        throw new Error(
          'Insights export is not available: host did not provide service.exportDataset'
        )
      }

      setIsExporting(true)
      try {
        const params: Record<string, string | string[] | number> = {
          ...serialiseFiltersForApi(filters),
          columns: columns.join(','),
          ...(columnGroups &&
            columnGroups.length > 0 && {
              columnGroups: columnGroups.join(','),
            }),
        }
        let csv: string
        try {
          const response = await service.exportDataset({
            domain,
            dataset,
            params,
          })
          csv = response.data
        } catch (error) {
          // Surface the API error code (e.g. export_too_large) so the caller can
          // show a specific message; the raw Axios error never leaves the hook.
          throw new InsightsExportError(exportErrorCode(error))
        }
        const rowCount = countCsvDataRows(csv)

        // Guard against a header-only download: the pre-export `totalResults`
        // check normally renders the empty state instead, but if the export
        // comes back empty anyway (filter race / endpoint disagreement), skip
        // the download and let the caller surface a "no data" message.
        if (rowCount === 0) {
          return 0
        }

        downloadCsv(fileName, csv)

        return rowCount
      } finally {
        setIsExporting(false)
      }
    },
    [service, filters, domain, dataset]
  )

  return { exportCsv, isExporting, canExport }
}
